/**
 * CompletedJobSummary (DISPATCH-DONE-VIEW, Seth 2026-08-12) — what a COMPLETED
 * Pegasus dispatch opens as: a read-only record of the visit (who ran it, when,
 * labor on site, what was done, what was billed, how it was paid, where it
 * posted) instead of the live editing surface. ESC locks an invoiced ticket
 * hard; competitors (ServiceTitan / Jobber / HCP) show a summary + an explicit,
 * audited Reopen. We do the summary + a GATED reopen ladder tied to the invoice:
 *   - no invoice / draft       → reopen freely (light confirm)
 *   - issued, unpaid, unposted → reopen returns the invoice to DRAFT (warning)
 *   - paid or posted to ESC    → LOCKED. Corrections = void + credit + reissue
 *     (write-spec §12); history is never edited in place.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BadgeCheck, Clock, FileText, Lock, MapPin, Phone, RotateCcw, Send,
  UserRound, Wrench, X, AlertTriangle, CheckCircle2, Landmark,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/Button";
import { Copyable, mailingLabel } from "@/components/Copyable";
import { formatDateUS, formatDateTimeUS } from "@/lib/format";
import { useStaffNames, staffFirst } from "@/lib/staffNames";
import { useCustomerPayments, invoicePaidMap, cardBrandLabel } from "@/features/payments/payments";
import { useInvoiceSendStatuses, InvoiceSentChip } from "@/features/comms/invoiceSend";
import {
  useDispatchEvents, useLogDispatchEvent, useSetTicketStatus, type PendingTicket,
} from "./queue";

const money = (n: number | null | undefined) =>
  (Number(n) || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

// ---------- data ----------

interface DoneInvoice {
  id: string;
  invoice_no: string | null;
  dispatch_no: number | null;
  status: string | null; // draft | issued
  technician: string | null;
  completed_date: string | null;
  tech_notes: string | null;
  internal_notes: string | null;
  total: number | null;
  created_at: string;
}
interface DoneLine {
  id: string;
  kind: string | null;
  description: string | null;
  qty: number | null;
  unit_price: number | null;
  amount: number | null;
  sort: number | null;
  print: boolean | null;
}

/** The dispatch's latest Pegasus invoice (any status) + its lines. */
function useDispatchInvoice(dispatchId: string) {
  return useQuery({
    queryKey: ["dispatch_invoice", dispatchId],
    queryFn: async (): Promise<{ inv: DoneInvoice | null; lines: DoneLine[] }> => {
      const { data, error } = await supabase
        .from("pegasus_job_invoice")
        .select("id, invoice_no, dispatch_no, status, technician, completed_date, tech_notes, internal_notes, total, created_at")
        .eq("dispatch_id", dispatchId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      const inv = (data ?? null) as DoneInvoice | null;
      if (!inv) return { inv: null, lines: [] };
      const { data: ls } = await supabase
        .from("pegasus_job_invoice_line")
        .select("id, kind, description, qty, unit_price, amount, sort, print")
        .eq("invoice_id", inv.id)
        .order("sort", { ascending: true });
      return { inv, lines: (ls ?? []) as DoneLine[] };
    },
    staleTime: 15 * 1000,
  });
}

/** Labor logged against this dispatch (hours on site until mobile clock-in lands). */
function useDispatchLabor(dispatchId: string) {
  return useQuery({
    queryKey: ["dispatch_labor", dispatchId],
    queryFn: async (): Promise<{ crew: string | null; hours: number }[]> => {
      const { data, error } = await supabase
        .from("pegasus_job_labor")
        .select("crew, hours")
        .eq("dispatch_id", dispatchId);
      if (error) throw error;
      return ((data ?? []) as { crew: string | null; hours: number | null }[]).map((r) => ({
        crew: r.crew,
        hours: Number(r.hours) || 0,
      }));
    },
    staleTime: 30 * 1000,
  });
}

/** Where (if anywhere) this invoice landed in ESC — the hard lock. */
function useEscPostNo(invoiceId: string | null | undefined) {
  return useQuery({
    queryKey: ["esc_post_no", invoiceId],
    enabled: Boolean(invoiceId),
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase
        .from("pegasus_esc_post")
        .select("esc_invoice_no")
        .eq("invoice_id", invoiceId as string)
        .maybeSingle();
      if (error) throw error;
      return (data as { esc_invoice_no: string } | null)?.esc_invoice_no ?? null;
    },
    staleTime: 30 * 1000,
  });
}

// ---------- bits ----------

function Row({ icon: Icon, label, children }: { icon: React.ComponentType<{ className?: string }>; label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
        <div className="text-sm text-slate-800">{children}</div>
      </div>
    </div>
  );
}

function Section({ title, right, children }: { title: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="overflow-hidden rounded-2xl border border-hairline bg-white shadow-e1">
      <header className="flex items-center justify-between gap-2 border-b border-hairline bg-slate-50/70 px-4 py-2">
        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{title}</h4>
        {right}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

// ---------- the view ----------

export function CompletedJobSummary({ live, onClose }: { live: PendingTicket; onClose: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setStatus = useSetTicketStatus();
  const log = useLogDispatchEvent();
  const staff = useStaffNames().data;
  const events = useDispatchEvents(live.id).data ?? [];
  const { data: invData } = useDispatchInvoice(live.id);
  const labor = useDispatchLabor(live.id).data ?? [];
  const payments = useCustomerPayments(live.custno).data;
  const inv = invData?.inv ?? null;
  const lines = invData?.lines ?? [];
  const escNo = useEscPostNo(inv?.id).data ?? null;
  // INVOICE-SENT-01: did the tech's invoice email go out? (outbox, not a flag)
  const sendStatus = useInvoiceSendStatuses([inv?.id]).data?.get(inv?.id ?? "");

  const [confirmReopen, setConfirmReopen] = useState(false);
  const [note, setNote] = useState("");

  // Paid math from the ledger (same derivation the print view uses — never a flag).
  const paidMap = invoicePaidMap(payments);
  const paid = inv ? paidMap.get(inv.id) ?? 0 : 0;
  const total = Number(inv?.total) || 0;
  const balance = Math.max(0, total - paid);
  const hasPayment = paid > 0.005;
  // The payments that touched this invoice, for the "how it was paid" line.
  const invPayments = (payments ?? []).filter(
    (p) => (p.pegasus_payment_allocation ?? []).some((a) => a.invoice_id === inv?.id),
  );
  const lastPay = invPayments[0] ?? null;
  const payLabel = lastPay
    ? lastPay.method === "card" || lastPay.method === "card_on_file"
      ? `${cardBrandLabel(lastPay.card_brand)} ····${lastPay.card_last4 ?? ""}`
      : lastPay.method === "check"
        ? `Check${lastPay.reference ? ` #${lastPay.reference}` : ""}`
        : lastPay.method.charAt(0).toUpperCase() + lastPay.method.slice(1)
    : null;

  const hours = labor.reduce((s, l) => s + l.hours, 0);
  const crewNames = [...new Set(labor.map((l) => l.crew).filter(Boolean))] as string[];

  // Completed when: prefer a logged status event; else the invoice's completed date.
  const doneEvent = [...events].reverse().find((e) => e.kind === "status_changed" && /completed/i.test(e.summary));
  const completedDate = inv?.completed_date ?? live.scheduled_date;

  // ---- the reopen ladder ----
  const locked = Boolean(escNo) || hasPayment;
  const issuedUnpaid = !locked && inv?.status === "issued";
  const lockReason = escNo
    ? `posted to ESC as invoice ${escNo}`
    : hasPayment
      ? `has ${money(paid)} in recorded payments`
      : null;

  const doReopen = async () => {
    setConfirmReopen(false);
    if (issuedUnpaid && inv) {
      const { error } = await supabase.from("pegasus_job_invoice").update({ status: "draft" }).eq("id", inv.id);
      if (error) { console.error("reopen: invoice → draft failed", error); return; }
      qc.invalidateQueries({ queryKey: ["dispatch_invoice", live.id] });
      qc.invalidateQueries({ queryKey: ["pegasus_customer_invoices"] });
    }
    setStatus.mutate({ id: live.id, status: "scheduled" });
    log.mutate({
      dispatch_id: live.id,
      kind: "status_changed",
      summary: issuedUnpaid ? "Reopened — invoice returned to draft" : "Reopened (Scheduled)",
    });
  };

  const addNote = () => {
    const t = note.trim();
    if (!t) return;
    log.mutate({ dispatch_id: live.id, kind: "note", summary: t });
    setNote("");
  };

  const visibleLines = lines.filter((l) => l.print !== false);
  const hiddenCount = lines.length - visibleLines.length;

  return (
    <div className="flex flex-col gap-4">
      {/* Identity + the completed banner — this is a RECORD, and it should read like one. */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
              <BadgeCheck className="h-3.5 w-3.5" /> Completed
            </span>
            <h3 className="truncate text-xl font-semibold text-slate-900">{live.customer_name}</h3>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-slate-600">
            {(live.address || live.city) && (
              <Copyable text={mailingLabel({ name: live.customer_name, address: live.address, city: live.city })} label="Copy name + address">
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5 text-slate-400" />
                  {[live.address, live.city].filter(Boolean).join(", ")}
                </span>
              </Copyable>
            )}
            {live.phone && (
              <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5 text-slate-400" /> {live.phone}</span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            {inv && (
              <button
                type="button"
                onClick={() => navigate(`/invoices/${inv.id}`)}
                className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-brand-700 px-3 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800"
              >
                <FileText className="h-3.5 w-3.5" /> View invoice
              </button>
            )}
            <button
              type="button"
              onClick={() => live.custno && navigate(`/customers/${live.custno}`)}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-hairline bg-white px-2.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50"
            >
              <UserRound className="h-3.5 w-3.5 text-slate-400" /> Open customer
            </button>
          </div>
          <span className="text-xs text-slate-400">
            {live.dispatch_no != null ? `Dispatch ${live.dispatch_no}` : `Ref ${live.id.slice(0, 8)}`}
          </span>
        </div>
      </div>

      {/* The visit at a glance — when, who, how long. */}
      <div className="grid grid-cols-2 gap-4 rounded-xl border border-emerald-100 bg-emerald-50/50 px-4 py-3 sm:grid-cols-4">
        <Row icon={CheckCircle2} label="Completed">
          {completedDate ? formatDateUS(completedDate) : "—"}
          {doneEvent && <div className="text-[11px] text-slate-500">{formatDateTimeUS(doneEvent.created_at)}</div>}
        </Row>
        <Row icon={UserRound} label="Technician">
          {inv?.technician || crewNames.join(", ") || live.assigned_empno || "—"}
        </Row>
        <Row icon={Clock} label="Time on site">
          {hours > 0 ? `${hours % 1 === 0 ? hours : hours.toFixed(1)} hr${hours === 1 ? "" : "s"}` : "—"}
          {hours > 0 && crewNames.length > 1 && (
            <div className="text-[11px] text-slate-500">{crewNames.length} techs</div>
          )}
        </Row>
        <Row icon={Wrench} label="Job">
          {live.service_type_name ?? live.summary ?? "Service call"}
        </Row>
      </div>

      <div className="grid grid-cols-1 gap-x-5 gap-y-4 lg:grid-cols-2">
        <div className="space-y-4">
          {/* What was done — the tech's story of the visit. */}
          <Section title="Work performed">
            {inv?.tech_notes ? (
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800">{inv.tech_notes}</p>
            ) : live.notes ? (
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800">{live.notes}</p>
            ) : (
              <p className="text-sm text-slate-400">No work summary recorded.</p>
            )}
            {inv?.internal_notes && (
              <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <span className="font-semibold">Office-only note:</span> {inv.internal_notes}
              </div>
            )}
          </Section>

          {/* What was billed + how it was paid. */}
          <Section
            title="Billing"
            right={
              escNo ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700">
                  <Landmark className="h-3 w-3" /> ESC #{escNo.replace(/^0+/, "")}
                </span>
              ) : inv ? (
                <span className="text-[11px] font-normal normal-case text-slate-400">not posted to ESC yet</span>
              ) : undefined
            }
          >
            {inv ? (
              <>
                <div className="space-y-1">
                  {visibleLines.map((l) => (
                    <div key={l.id} className="flex items-baseline justify-between gap-3 text-sm">
                      <span className="min-w-0 truncate text-slate-700">
                        {Number(l.qty) > 1 ? `${Number(l.qty)} × ` : ""}{l.description ?? "—"}
                      </span>
                      <span className="shrink-0 tabular-nums text-slate-800">{money(l.amount)}</span>
                    </div>
                  ))}
                  {hiddenCount > 0 && (
                    <div className="text-[11px] italic text-slate-400">+ {hiddenCount} hidden line{hiddenCount === 1 ? "" : "s"} (not printed)</div>
                  )}
                </div>
                <div className="mt-2.5 flex items-baseline justify-between border-t border-hairline pt-2">
                  <span className="text-sm font-semibold text-slate-900">Total</span>
                  <span className="text-base font-bold tabular-nums text-slate-900">{money(total)}</span>
                </div>
                <div className="mt-1.5 flex items-center justify-between text-sm">
                  {balance <= 0.005 && hasPayment ? (
                    <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-700">
                      <CheckCircle2 className="h-4 w-4" /> Paid in full{payLabel ? ` · ${payLabel}` : ""}
                    </span>
                  ) : hasPayment ? (
                    <span className="font-semibold text-amber-700">
                      {money(paid)} paid{payLabel ? ` (${payLabel})` : ""} · {money(balance)} due
                    </span>
                  ) : (
                    <span className="font-semibold text-amber-700">Unpaid · {money(balance)} due</span>
                  )}
                  <span className="text-[11px] uppercase tracking-wide text-slate-400">{inv.status}</span>
                </div>
                {/* Sent to the customer? Issued invoices only — a draft has nothing to send. */}
                {inv.status === "issued" && (
                  <div className="mt-1.5 flex items-center justify-between gap-2 text-sm">
                    <span className="text-[11px] text-slate-400">Sent to customer</span>
                    <InvoiceSentChip status={sendStatus} onSend={() => navigate(`/invoices/${inv.id}?email=1`)} className="max-w-[300px]" />
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-400">No invoice on this dispatch.</p>
            )}
          </Section>
        </div>

        {/* The audit trail stays visible — and notes can still be appended (append-only). */}
        <Section title="Activity">
          <div className="max-h-64 overflow-auto pr-1">
            {events.length === 0 && <p className="text-sm text-slate-400">No activity recorded.</p>}
            {events.map((e, i) => (
              <div key={e.id} className={cn("relative flex gap-3 pl-6", i !== events.length - 1 && "ml-[9px] border-l-[1.5px] border-hairline pb-3")}>
                <span className="absolute -left-[9px] top-0 h-[18px] w-[18px] rounded-full bg-slate-100" />
                <div className="min-w-0">
                  <div className="text-sm text-slate-800">{e.summary}</div>
                  <div className="text-[11px] text-slate-400">{formatDateTimeUS(e.created_at)} · {staffFirst(staff, e.actor)}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addNote(); }}
              placeholder="Add a note to the record…"
              className="flex-1 rounded-md border border-hairline px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
            <Button variant="secondary" size="sm" disabled={!note.trim()} onClick={addNote}>
              <Send className="h-3.5 w-3.5" /> Add note
            </Button>
          </div>
        </Section>
      </div>

      {/* REOPEN — the gated ladder. Locked records explain WHY and point at the
          correction path instead of silently hiding the button. */}
      <div className="rounded-xl border border-hairline bg-slate-50/70 px-4 py-3">
        {locked ? (
          <div className="flex items-start gap-2.5">
            <Lock className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            <div className="text-sm text-slate-600">
              <span className="font-semibold text-slate-800">This ticket is locked.</span>{" "}
              The invoice {lockReason}. Reopening would rewrite history the books already
              recorded — corrections happen by voiding in ESC and issuing a corrected
              invoice, so the paper trail stays honest.
            </div>
          </div>
        ) : confirmReopen ? (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="flex min-w-0 items-center gap-1.5 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {issuedUnpaid
                ? "Reopen this call? The issued invoice goes back to DRAFT and the ticket returns to the active board."
                : "Reopen this call? It returns to the active board as Scheduled."}
            </span>
            <div className="flex shrink-0 items-center gap-1.5">
              <button
                type="button"
                onClick={doReopen}
                className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-amber-700"
              >
                <RotateCcw className="h-3.5 w-3.5" /> Yes, reopen
              </button>
              <button
                type="button"
                onClick={() => setConfirmReopen(false)}
                className="rounded-md bg-white px-2.5 py-1 text-xs font-medium text-slate-600 shadow-e1 transition-colors hover:bg-slate-50"
              >
                Keep completed
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm text-slate-500">
              Completed by mistake, or the visit isn't actually done?
              {issuedUnpaid && " (The issued invoice would return to draft.)"}
            </span>
            <button
              type="button"
              onClick={() => setConfirmReopen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-hairline bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Reopen this call
            </button>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <Button variant="secondary" size="sm" onClick={onClose}>
          <X className="h-3.5 w-3.5" /> Close
        </Button>
      </div>
    </div>
  );
}
